import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getPermittedActions } from '../../../redux/project/sliceProjectActions';
import { getAreasExtendedConfigByField } from '../../../redux/project/sliceProjectAreas';
import ContextMenuItem from '../../General/ContextMenu/ContextMenuItem';
import DropdownMenu from '../../General/DropdownMenu/DropdownMenu';
import { mdiPlusCircleOutline } from '@mdi/js';
import { devOnClick, docTableActionsCallbacks } from '../../General/DocTable/DocTableActions';
import { IAppReduxStore } from '../../../redux/store';

export default () => {
    /** Configure component translation. */
    const { i18n, t } = useTranslation('translations', { keyPrefix: 'projectView' });

    /** Get the areaName and viewName from the react router. */
    const { replicaId, areaName, viewName } = useParams();

    /** Get relevant values from Redux store. */
    const actionsList = useSelector(getPermittedActions);
    const availableCreationActions = useSelector(
        getAreasExtendedConfigByField(areaName, 'CreationActions'),
    );
    const pendingActions = useSelector((store: IAppReduxStore) => store.actionHandling.actions);

    /**
     * Returns available action's list for the creation actions menu.
     */
    const getAvailableCreationActionsList = useMemo(() => {
        if (!actionsList || !availableCreationActions) return null;

        return availableCreationActions
            .map(action => actionsList.find(item => item.name === action))
            .filter(Boolean);
    }, [availableCreationActions, actionsList]);

    /**
     * Returns available items for creation actions menu.
     */
    const menuItems = useMemo(() => {
        if (!getAvailableCreationActionsList?.length) return null;

        return (
            getAvailableCreationActionsList
                .map(({ name, icon, translations }) => {
                    return (
                        <ContextMenuItem
                            disabled={(pendingActions ?? []).includes(name)}
                            key={name}
                            text={
                                name === 'createFilingCategory'
                                    ? t(`create_${viewName}`)
                                    : translations[i18n.language]
                            }
                            icon={icon}
                            onClick={() =>
                                (docTableActionsCallbacks[name] ?? devOnClick)({
                                    replicaId,
                                    areaName,
                                    viewName,
                                })
                            }
                        />
                    );
                })
                /** Remove any null items from the array. */
                .filter(Boolean)
        );
    }, [getAvailableCreationActionsList, pendingActions]);

    return menuItems?.length ? (
        <DropdownMenu title={t('create')} icon={mdiPlusCircleOutline}>
            {menuItems}
        </DropdownMenu>
    ) : null;
};
